import { Controller } from "@hotwired/stimulus"

/**
 * Tracks which users were @mentioned in the Lexxy reply editor and submits
 * their ids in a hidden field, separate from the comment body.
 *
 * data-controller="support--mention-tracker"
 *
 * WHY THE IDS DO NOT LIVE IN THE BODY
 * Lexical rebuilds whatever the prompt template contains into its own nodes —
 * it drops a <span class> and a <mark> alike (verified in a browser: selecting
 * a mention yields `<span data-lexical-text="true">@James Wilson</span>`). No
 * wrapper of ours survives, so identity cannot ride along inside the text. It
 * also should not: that body is rendered to the CUSTOMER on the portal timeline
 * and re-exported into other tenants' LLM prompts.
 *
 * WHY SELECTION IS RECORDED, NOT INFERRED
 * An earlier version recovered ids by searching the editor text for each
 * candidate's name. That does not survive a real directory: two people called
 * "Ana García" collide on the same name; "@Ana María" literally contains
 * "@Ana", so mentioning one would notify the other; and typing "@Juan" by hand
 * without picking from the list would silently notify a Juan the author never
 * chose. With ~2,000 users those are everyday cases, not edge cases.
 *
 * So the id is captured at the moment of SELECTION — the one point where the
 * choice is unambiguous — and kept only while the text it inserted is still
 * present. Lexxy dispatches no "item selected" event and its handler is a
 * private method, so selection is observed the way the DOM exposes it: the
 * highlighted row carries `aria-selected`, and a commit (Enter, Tab or click)
 * is what turns that row into an insertion.
 *
 * The hidden field is a CONVENIENCE, never an authority. The server re-resolves
 * every id against the ticket's authorization rules (Support::MentionExtraction),
 * so the worst a wrong or tampered id can do is name someone who is then
 * filtered out — it can never notify someone who should not be reachable.
 */
export default class extends Controller {
  static targets = ["editor", "field"]

  connect() {
    // Mentions actually committed: [{ id, name, text }]
    this._mentions = []
    // The row currently highlighted in the popover, if any.
    this._pending = null

    this._onLexxyChange = this._onLexxyChange.bind(this)
    this._onMenuMutate = this._onMenuMutate.bind(this)
    this._onCommitKey = this._onCommitKey.bind(this)
    this._onMenuClick = this._onMenuClick.bind(this)

    this.element.addEventListener("lexxy:change", this._onLexxyChange)
    // Capture phase: Lexxy's own handler stops/consumes these, so we must see
    // the key before it acts on it.
    this.element.addEventListener("keydown", this._onCommitKey, true)
    this.element.addEventListener("mousedown", this._onMenuClick, true)

    this._observer = new MutationObserver(this._onMenuMutate)
    this._observer.observe(this.element, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["aria-selected"],
    })
  }

  disconnect() {
    this.element.removeEventListener("lexxy:change", this._onLexxyChange)
    this.element.removeEventListener("keydown", this._onCommitKey, true)
    this.element.removeEventListener("mousedown", this._onMenuClick, true)
    this._observer?.disconnect()
  }

  // Remember which row the pointer/keyboard is on, reading the id straight off
  // the row. The id lives in the menu template (see _mention_prompt_items),
  // because <lexxy-prompt-item> elements are parsed into memory and never
  // reach the document — querySelectorAll finds none of them.
  _onMenuMutate() {
    const menu = this.element.querySelector(".lexxy-prompt-menu")
    // Popover gone (dismissed, clicked away, filtered to nothing) means the
    // highlighted row was never committed — drop it rather than let it survive
    // until some later Enter picks up a stale choice. Harmless either way, since
    // the server re-resolves every id, but a stale _pending is a confusing thing
    // to leave lying around.
    if (!menu || !menu.classList.contains("lexxy-prompt-menu--visible")) {
      this._pending = null
      return
    }
    // aria-selected is set as a PROPERTY by Lexxy, not an attribute, so
    // getAttribute cannot see it; read the property instead.
    const row = [...menu.querySelectorAll("li")].find(
      (li) => li.ariaSelected === "true" || li.ariaSelected === true,
    )
    this._pending = this._mentionFromRow(row) || this._pending
  }

  _mentionFromRow(row) {
    const holder = row?.querySelector("[data-mention-user-id]")
    if (!holder) return null
    const id = holder.dataset.mentionUserId
    const name = holder.dataset.mentionName
    return id && name ? { id, name } : null
  }

  // Enter/Tab commit the highlighted row; Escape abandons it.
  _onCommitKey(event) {
    if (event.key === "Escape") {
      this._pending = null
      return
    }
    if (event.key !== "Enter" && event.key !== "Tab") return
    const menu = this.element.querySelector(".lexxy-prompt-menu--visible")
    if (!menu) return
    // Fall back to the only row on screen: with a narrow filter there is often
    // exactly one, and Lexxy may commit it without ever flagging it selected.
    if (!this._pending) {
      const rows = [...menu.querySelectorAll("li")]
      if (rows.length === 1) this._pending = this._mentionFromRow(rows[0])
    }
    this._commitPending()
  }

  _onMenuClick(event) {
    const row = event.target.closest?.(".lexxy-prompt-menu__item")
    if (!row) return
    // The row under the pointer wins over whatever the keyboard had highlighted.
    this._pending = this._mentionFromRow(row) || this._pending
    this._commitPending()
  }

  _commitPending() {
    if (!this._pending) return
    // Same person mentioned twice is one notification, but both occurrences
    // are tracked so deleting one does not drop the other.
    this._mentions.push({ id: this._pending.id, text: this._pending.name })
    this._pending = null
    // The insertion happens after this tick; sync once it has landed.
    requestAnimationFrame(() => this._syncField())
  }

  _onLexxyChange() {
    this._syncField()
  }

  // Keep a mention only while the exact text it inserted is still in the body,
  // consuming each match so two mentions of the same person need two
  // occurrences. Deleting the text removes the id from the payload.
  _syncField() {
    if (!this.hasFieldTarget) return

    const text = this._editorText()
    const ids = []
    const taken = []

    // Longest first so "@Ana María" claims its characters before "@Ana" can.
    const ordered = [...this._mentions].sort(
      (a, b) => b.text.length - a.text.length,
    )

    for (const mention of ordered) {
      let from = 0
      for (;;) {
        const at = text.indexOf(mention.text, from)
        if (at === -1) break
        const end = at + mention.text.length
        if (!taken.some(([s, e]) => at < e && end > s)) {
          taken.push([at, end])
          if (!ids.includes(mention.id)) ids.push(mention.id)
          break
        }
        from = at + 1
      }
    }

    this._writeIds(ids)
  }

  // ONLY the contenteditable body. Reading textContent off <lexxy-editor>
  // itself sweeps in the whole toolbar — every heading label, every code
  // language, the table menus — so a mention's text was drowned in ~2KB of
  // chrome and the match never landed where it should.
  _editorText() {
    const editor = this.hasEditorTarget
      ? this.editorTarget
      : this.element.querySelector("lexxy-editor")
    const body = editor?.querySelector("[contenteditable]")
    return body?.textContent || ""
  }

  // One hidden input per id, so Rails parses the field as an array.
  _writeIds(ids) {
    const container = this.fieldTarget
    const name = container.dataset.fieldName
    container.innerHTML = ""
    for (const id of ids) {
      const input = document.createElement("input")
      input.type = "hidden"
      input.name = name
      input.value = id
      container.appendChild(input)
    }
  }
}
